import { type FormControlLabelProps } from '@mui/material/FormControlLabel'
import {
  type FormFieldDescriptor,
  type NarrowFieldsByValueType,
  type TidalFormBridgeOptions,
  type TidalFormState,
  useTidalFormBridge,
} from '@workwave-tidal/core/form'

import { RecurrencePicker } from './RecurrencePicker'
import type { RecurrencesMap } from './typings'

export interface RecurrencePickerProps {
  recurrenceMap: RecurrencesMap
}

export type RecurrencePickerFieldValue = uui.domain.rm.Recurrence | undefined
const defaultComponentValue: RecurrencePickerFieldValue = undefined

export type RecurrencePickerFieldProps<
  TargetField extends NarrowFieldsByValueType<AcceptedFormValue, State, FormFieldDescriptor<State>>,
  State extends TidalFormState,
  AcceptedFormValue = RecurrencePickerFieldValue,
> = {
  recurrencePickerProps: RecurrencePickerProps
  formControlLabelProps?: Partial<FormControlLabelProps>
} & TidalFormBridgeOptions<RecurrencePickerFieldValue, TargetField, State, AcceptedFormValue>

export function RecurrencePickerField<
  TargetField extends NarrowFieldsByValueType<AcceptedFormValue, State, FormFieldDescriptor<State>>,
  State extends TidalFormState,
  AcceptedFormValue = RecurrencePickerFieldValue,
>(props: RecurrencePickerFieldProps<TargetField, State, AcceptedFormValue>) {
  const {
    recurrencePickerProps: { recurrenceMap },
    formControlLabelProps = {},
    initialValue = defaultComponentValue as FormFieldDescriptor<State>[TargetField]['value'],
    validateOn = 'change',
    ...bridgeOptions
  } = props

  const {
    fieldInfo: {
      field: { visible },
      onChange,
      value,
    },
  } = useTidalFormBridge<RecurrencePickerFieldValue, TargetField, State, AcceptedFormValue>({
    ...bridgeOptions,
    validateOn,
    initialValue,
  })

  if (!visible) return null

  return <RecurrencePicker onChange={onChange} value={value} recurrenceMap={recurrenceMap} />
}
